import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { InterviewConfig, Message, BehaviorSummary } from "../types";

// 面试报告类型
export interface InterviewReport {
  totalScore: number;
  dimensions: {
    expression: number;
    technical: number;
    logic: number;
    communication: number;
    professionalism: number;
    learning: number;
    overall: number;
  };
  strengths: string[];
  suggestions: {
    category: string;
    content: string;
  }[];
  detailedAnalysis: {
    expression: string;
    technical: string;
    logic: string;
    communication: string;
    professionalism: string;
    learning: string;
  };
  nextSteps: string[];
}

// 面试状态
export interface InterviewState {
  // 面试配置
  config: InterviewConfig | null;
  // 消息记录
  messages: Message[];
  // 面试报告
  report: InterviewReport | null;
  // 是否正在面试中
  isInterviewing: boolean;
  // 是否正在加载
  isLoading: boolean;
  // 开场白音频URL
  welcomeAudioUrl: string | null;
  // 行为分析摘要
  behaviorSummary: BehaviorSummary | null;
}

// 初始状态
const initialState: InterviewState = {
  config: null,
  messages: [],
  report: null,
  isInterviewing: false,
  isLoading: false,
  welcomeAudioUrl: null,
  behaviorSummary: null,
};

const interviewSlice = createSlice({
  name: "interview",
  initialState,
  reducers: {
    // 设置面试配置
    setInterviewConfig: (state, action: PayloadAction<InterviewConfig>) => {
      state.config = action.payload;
    },
    // 添加消息
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },
    // 设置消息列表
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload;
    },
    // 设置面试报告
    setInterviewReport: (state, action: PayloadAction<InterviewReport>) => {
      state.report = action.payload;
    },
    // 设置面试中状态
    setIsInterviewing: (state, action: PayloadAction<boolean>) => {
      state.isInterviewing = action.payload;
    },
    // 设置加载状态
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // 开始面试
    startInterview: (state, action: PayloadAction<InterviewConfig>) => {
      state.config = action.payload;
      state.messages = [];
      state.report = null;
      state.isInterviewing = true;
      state.isLoading = false;
    },
    // 结束面试
    endInterview: (state, action: PayloadAction<InterviewReport>) => {
      state.report = action.payload;
      state.isInterviewing = false;
    },
    // 设置行为分析摘要
    setBehaviorSummary: (
      state,
      action: PayloadAction<BehaviorSummary | null>,
    ) => {
      state.behaviorSummary = action.payload;
    },
    // 重置面试状态
    resetInterview: (state) => {
      state.config = null;
      state.messages = [];
      state.report = null;
      state.isInterviewing = false;
      state.isLoading = false;
      state.welcomeAudioUrl = null;
      state.behaviorSummary = null;
    },
    // 重置面试数据（保留配置，用于再来一次）
    resetInterviewData: (state) => {
      state.messages = [];
      state.report = null;
      state.isInterviewing = false;
      state.isLoading = false;
      state.welcomeAudioUrl = null;
      state.behaviorSummary = null;
    },
    // 设置开场白音频URL
    setWelcomeAudioUrl: (state, action: PayloadAction<string | null>) => {
      state.welcomeAudioUrl = action.payload;
    },
  },
});

// 导出 actions
export const {
  setInterviewConfig,
  addMessage,
  setMessages,
  setInterviewReport,
  setIsInterviewing,
  setIsLoading,
  startInterview,
  endInterview,
  setBehaviorSummary,
  resetInterview,
  resetInterviewData,
  setWelcomeAudioUrl,
} = interviewSlice.actions;

// 导出 reducer
export default interviewSlice.reducer;
